/**
 * user-role の型 User（02 5.3.2）。①は型と検証、⑦で利用者の CRUD API（API-USER-02〜08）に使う。
 */
import { z } from 'zod';
import { zCode, zEmail, zRequiredText, zShortText, zVersion } from './common.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

/** 利用者のメール（空は MSG-V-039「メールアドレスは必須です」：03 SCR-073） */
const zUserEmail = z.string({ message: 'MSG-V-039' }).trim().min(1, 'MSG-V-039').pipe(zEmail);

export const UserSchema = z
  .object({
    user_code: z.string(),
    email: z.string(),
    user_name: z.string(),
    affiliated_company_name: z.string().nullable(),
    role_code: z.string(),
    role_name: z.string(),
    is_active: z.boolean(),
    last_login_at: z.string().nullable(),
    deleted_at: z.string().nullable().optional(),
    delete_batch_code: z.string().nullable().optional(),
  })
  .merge(CommonAttributesSchema);

export const UserCreateSchema = z
  .object({
    email: zUserEmail,
    user_name: zRequiredText(100, 'MSG-V-064'),
    affiliated_company_name: zShortText(200).optional(),
    role_code: zCode,
    is_active: z.boolean().default(true),
  })
  .strict();

export const UserUpdateSchema = z
  .object({
    email: zUserEmail.optional(),
    user_name: zRequiredText(100, 'MSG-V-064').optional(),
    affiliated_company_name: zShortText(200).optional(),
    role_code: zCode.optional(),
    is_active: z.boolean().optional(),
    version: zVersion,
  })
  .strict();

export const USER_LIST_SORT_VALUES = ['user_name', 'email', 'created_at'] as const;

export const UserListQuerySchema = buildListQuerySchema(USER_LIST_SORT_VALUES, 'user_name').extend({
  role_code: zCode.optional(),
  is_active: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
});

export type User = z.infer<typeof UserSchema>;
export type UserCreateInput = z.infer<typeof UserCreateSchema>;
export type UserUpdateInput = z.infer<typeof UserUpdateSchema>;

/** API-USER-08 SES 検証メールの一括送信（1〜100 件・重複不可） */
export const UserEmailVerificationRequestSchema = z
  .object({
    user_codes: z
      .array(zCode)
      .min(1, 'MSG-V-020')
      .max(100, 'MSG-V-020')
      .refine((codes) => new Set(codes).size === codes.length, 'MSG-V-020'),
  })
  .strict();

export const USER_EMAIL_VERIFICATION_RESULT_VALUES = ['sent', 'alreadyVerified', 'notRequired', 'failed'] as const;

export const UserEmailVerificationResponseSchema = z.object({
  results: z.array(
    z.object({ user_code: z.string(), email: z.string(), result: z.enum(USER_EMAIL_VERIFICATION_RESULT_VALUES) }),
  ),
  sent_count: z.number().int(),
  fail_count: z.number().int(),
});

export type UserEmailVerificationRequest = z.infer<typeof UserEmailVerificationRequestSchema>;
export type UserEmailVerificationResponse = z.infer<typeof UserEmailVerificationResponseSchema>;
